import type { IBaseFile } from '@/modules/common/models/file/base-file.ts';
import { notification } from '@/modules/common/composables/use-notification.ts';
import { formatFileSize } from '@/modules/common/composables/use-formatted-file-size.ts';

function getSourceName(file: IBaseFile): string {
  const prefix = file.systemPrefix;

  return prefix && !file.name.startsWith(prefix) ? prefix + file.name : file.name;
}

function findByName<T extends IBaseFile>(files: T[], name: string) {
  return files.find((current) => current.name === name);
}

function resolveNameConflict<T extends IBaseFile>(files: T[], file: T) {
  const conflicting = findByName(files, file.name);

  if (!conflicting) {
    return;
  }

  if (file.prefixAutoStripped) {
    file.restoreSystemPrefix();
    notification.warning({
      key: 'ui.common.notification.warning.file.systemPrefixKept',
      params: { name: file.name },
    });

    return;
  }

  if (conflicting.prefixAutoStripped) {
    conflicting.restoreSystemPrefix();
    notification.warning({
      key: 'ui.common.notification.warning.file.systemPrefixRestored',
      params: { name: conflicting.name },
    });
  }
}

export function mergeUniqueFiles<T extends IBaseFile>(existing: T[], ...incoming: T[][]): T[] {
  const result = [...existing];

  incoming.flat().forEach((file) => {
    const sourceName = getSourceName(file);
    const added = result.some((current) => getSourceName(current) === sourceName);

    if (!added) {
      resolveNameConflict(result, file);
    }

    if (added || findByName(result, file.name)) {
      notification.warning({
        key: 'ui.common.notification.warning.file.alreadyAdded',
        params: { name: file.name, size: formatFileSize(file.size) },
      });

      return;
    }

    result.push(file);
  });

  return result;
}
